import DashboardLayout from "@/components/DashboardLayout";
import { useState, useEffect } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, Check, AlertCircle, Bot, Plus, X } from "lucide-react";
import ApiKeyManagement from "@/components/ApiKeyManagement";
import ApiUsageStats from "@/components/ApiUsageStats";

// 靜態選項，可依需求擴充
const COUNTRIES = [
  { value: "tw", label: "台灣" },
  { value: "us", label: "美國" },
  { value: "jp", label: "日本" },
  { value: "kr", label: "韓國" },
];

const LOCALES = [
  { value: "zh-TW", label: "中文 (繁體)" },
  { value: "en-US", label: "English (US)" },
  { value: "ja-JP", label: "日本語" },
  { value: "ko-KR", label: "한국어" },
];

export default function Settings() {
  // Auth gating is handled by DashboardLayout; avoid hard redirects that can cause login loops.
  const { user } = useAuth();
  const [brand, setBrand] = useState("");
  const [country, setCountry] = useState("");
  const [locale, setLocale] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<"idle" | "success" | "error">("idle");
  
  // LLM Citation 設定
  const [llmBrandName, setLlmBrandName] = useState("");
  const [llmBrandAliases, setLlmBrandAliases] = useState<string[]>([]);
  const [newAlias, setNewAlias] = useState("");
  const [llmCheckFrequency, setLlmCheckFrequency] = useState("weekly");
  const [llmEnableGemini, setLlmEnableGemini] = useState(true);
  const [llmEnableChatgpt, setLlmEnableChatgpt] = useState(false);
  const [llmDailyCheckLimit, setLlmDailyCheckLimit] = useState(50);
  const [llmSaveStatus, setLlmSaveStatus] = useState<"idle" | "success" | "error">("idle");

  // 讀取設定值
  const brandQuery = trpc.settings.get.useQuery({ key: "brand_name" }, { enabled: !!user });
  const countryQuery = trpc.settings.get.useQuery({ key: "country" }, { enabled: !!user });
  const localeQuery = trpc.settings.get.useQuery({ key: "locale" }, { enabled: !!user });

  const upsertMutation = trpc.settings.upsert.useMutation({
    onSuccess: () => {
      setSaveStatus("success");
      setTimeout(() => setSaveStatus("idle"), 3000);
    },
    onError: () => {
      setSaveStatus("error");
      setTimeout(() => setSaveStatus("idle"), 3000);
    },
  });
  
  // LLM Citation queries and mutations
  const { data: llmSettings } = trpc.llmCitation.getSettings.useQuery(undefined, { enabled: !!user });
  const updateLlmSettings = trpc.llmCitation.updateSettings.useMutation({
    onSuccess: () => {
      setLlmSaveStatus("success");
      setTimeout(() => setLlmSaveStatus("idle"), 3000);
    },
    onError: () => {
      setLlmSaveStatus("error");
      setTimeout(() => setLlmSaveStatus("idle"), 3000);
    },
  });

  useEffect(() => {
    if (brandQuery.data?.value) setBrand(brandQuery.data.value);
    if (countryQuery.data?.value) setCountry(countryQuery.data.value);
    if (localeQuery.data?.value) setLocale(localeQuery.data.value);
  }, [brandQuery.data, countryQuery.data, localeQuery.data]);
  
  useEffect(() => {
    if (llmSettings) {
      setLlmBrandName(llmSettings.brandName || "");
      setLlmBrandAliases(llmSettings.brandAliases || []);
      setLlmCheckFrequency(llmSettings.checkFrequency || "weekly");
      setLlmEnableGemini(llmSettings.enableGemini ?? true);
      setLlmEnableChatgpt(llmSettings.enableChatgpt ?? false);
      setLlmDailyCheckLimit(llmSettings.dailyCheckLimit || 50);
    }
  }, [llmSettings]);

  const handleSaveAll = async () => {
    setIsSaving(true);
    try {
      // 依序 upsert 三個設定
      await upsertMutation.mutateAsync({ key: "brand_name", value: brand, description: "brand_name 設定" });
      await upsertMutation.mutateAsync({ key: "country", value: country, description: "country 設定" });
      await upsertMutation.mutateAsync({ key: "locale", value: locale, description: "locale 設定" });
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleAddAlias = () => {
    if (newAlias.trim() && !llmBrandAliases.includes(newAlias.trim())) {
      setLlmBrandAliases([...llmBrandAliases, newAlias.trim()]);
      setNewAlias("");
    }
  };
  
  const handleRemoveAlias = (alias: string) => {
    setLlmBrandAliases(llmBrandAliases.filter(a => a !== alias));
  };
  
  const handleSaveLlmSettings = async () => {
    await updateLlmSettings.mutateAsync({
      brandName: llmBrandName,
      brandAliases: llmBrandAliases,
      checkFrequency: llmCheckFrequency as 'never' | 'daily' | 'weekly' | 'monthly',
      enableGemini: llmEnableGemini,
      enableChatgpt: llmEnableChatgpt,
      dailyCheckLimit: llmDailyCheckLimit
    });
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <DashboardLayout>
      <div className="container max-w-4xl py-8">
        <div className="space-y-6">
          {/* 返回首頁 */}
          <div className="flex justify-between items-center">
            <h1 className="text-3xl font-bold">設定</h1>
            <a href="/" className="text-primary hover:underline">
              ← 返回首頁
            </a>
          </div>
          <p className="text-muted-foreground mt-2">管理系統設定與 API 金鑰</p>

          {/* 基本資訊：品牌、國家、語系 */}
          <Card>
            <CardHeader>
              <CardTitle>基本資訊</CardTitle>
              <CardDescription>設定品牌名稱、目標國家與語系（供 SerpAPI 使用）</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="brand-name">品牌名稱</Label>
                <Input id="brand-name" value={brand} onChange={e => setBrand(e.target.value)} disabled={isSaving} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="country-select">國家</Label>
                <select
                  id="country-select"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={country}
                  onChange={e => setCountry(e.target.value)}
                  disabled={isSaving}
                >
                  <option value="">請選擇國家</option>
                  {COUNTRIES.map(c => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="locale-select">語系</Label>
                <select
                  id="locale-select"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={locale}
                  onChange={e => setLocale(e.target.value)}
                  disabled={isSaving}
                >
                  <option value="">請選擇語系</option>
                  {LOCALES.map(l => (
                    <option key={l.value} value={l.value}>
                      {l.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end">
                <Button onClick={handleSaveAll} disabled={isSaving || !brand || !country || !locale}>
                  {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  儲存設定
                </Button>
              </div>
              {saveStatus === "success" && (
                <Alert className="bg-green-50 border-green-200">
                  <Check className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-800">設定已儲存</AlertDescription>
                </Alert>
              )}
              {saveStatus === "error" && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>儲存失敗，請稍後再試</AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* API 金鑰管理（多組 SerpAPI 金鑰） */}
          <Card>
            <CardHeader>
              <CardTitle>API 金鑰管理</CardTitle>
              <CardDescription>管理多組 SerpAPI 金鑰，支援自動輪替</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <ApiKeyManagement />
            </CardContent>
          </Card>

          {/* API 使用量統計 */}
          <Card>
            <CardHeader>
              <CardTitle>API 使用量</CardTitle>
              <CardDescription>追蹤 API Token 消耗與費用</CardDescription>
            </CardHeader>
            <CardContent>
              <ApiUsageStats />
            </CardContent>
          </Card>

          {/* LLM 引用監控設定 */}
          <Card className="border-purple-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bot className="h-5 w-5 text-purple-600" />
                LLM 引用監控設定
              </CardTitle>
              <CardDescription>設定品牌名稱與 LLM 檢查參數</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* 品牌名稱 */}
              <div className="space-y-2">
                <Label htmlFor="llm-brand-name">品牌名稱 *</Label>
                <Input
                  id="llm-brand-name"
                  value={llmBrandName}
                  onChange={e => setLlmBrandName(e.target.value)}
                  placeholder="例如：悦庭牙醫"
                  disabled={updateLlmSettings.isPending}
                />
                <p className="text-xs text-muted-foreground">
                  用於在 LLM 回應中檢測品牌提及
                </p>
              </div>

              {/* 品牌別名 */}
              <div className="space-y-2">
                <Label>品牌別名（可選）</Label>
                <div className="space-y-2">
                  {llmBrandAliases.map((alias, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Input value={alias} disabled className="flex-1" />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveAlias(alias)}
                        disabled={updateLlmSettings.isPending}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <div className="flex items-center gap-2">
                    <Input
                      value={newAlias}
                      onChange={e => setNewAlias(e.target.value)}
                      placeholder="輸入別名，例如：悦庭"
                      onKeyPress={e => e.key === 'Enter' && handleAddAlias()}
                      disabled={updateLlmSettings.isPending}
                      className="flex-1"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleAddAlias}
                      disabled={!newAlias.trim() || updateLlmSettings.isPending}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      新增
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  設定品牌的其他稱呼方式，提高檢測準確度
                </p>
              </div>

              {/* 檢查頻率 */}
              <div className="space-y-2">
                <Label htmlFor="llm-check-frequency">自動檢查頻率</Label>
                <select
                  id="llm-check-frequency"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={llmCheckFrequency}
                  onChange={e => setLlmCheckFrequency(e.target.value)}
                  disabled={updateLlmSettings.isPending}
                >
                  <option value="never">從不自動檢查</option>
                  <option value="daily">每日</option>
                  <option value="weekly">每週</option>
                  <option value="monthly">每月</option>
                </select>
              </div>

              {/* 啟用的 LLM */}
              <div className="space-y-3">
                <Label>啟用的 LLM 平台</Label>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={llmEnableGemini}
                      onChange={e => setLlmEnableGemini(e.target.checked)}
                      disabled={updateLlmSettings.isPending}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm">啟用 Gemini 檢查</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={llmEnableChatgpt}
                      onChange={e => setLlmEnableChatgpt(e.target.checked)}
                      disabled={updateLlmSettings.isPending}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm">啟用 ChatGPT 檢查（需要 API Key）</span>
                  </label>
                </div>
              </div>

              {/* 每日檢查上限 */}
              <div className="space-y-2">
                <Label htmlFor="llm-daily-limit">每日檢查上限</Label>
                <Input
                  id="llm-daily-limit"
                  type="number"
                  min="1"
                  max="200"
                  value={llmDailyCheckLimit}
                  onChange={e => setLlmDailyCheckLimit(parseInt(e.target.value) || 50)}
                  disabled={updateLlmSettings.isPending}
                />
                <p className="text-xs text-muted-foreground">
                  避免 API 費用過高，建議設定為 50
                </p>
              </div>

              {/* 儲存按鈕 */}
              <div className="flex justify-end">
                <Button
                  onClick={handleSaveLlmSettings}
                  disabled={updateLlmSettings.isPending || !llmBrandName}
                >
                  {updateLlmSettings.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  儲存 LLM 設定
                </Button>
              </div>

              {/* 狀態提示 */}
              {llmSaveStatus === "success" && (
                <Alert className="bg-green-50 border-green-200">
                  <Check className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-800">LLM 設定已儲存</AlertDescription>
                </Alert>
              )}
              {llmSaveStatus === "error" && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>儲存失敗，請稍後再試</AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
